import { useState } from 'react'
import { Link } from 'react-router'
import { FormattedDate, FormattedMessage, FormattedTime, useIntl } from 'react-intl'
import { useFetchCb } from '@gluedigital/ruse-fetch-extras'
import { useCompanies } from '@hooks/useCompanies'
import { useToast } from '@hooks/useToast'
import CompanyForm from '@components/CompanyForm/CompanyForm'
import { ADMIN_URL } from 'src/constants/global'
import type { Company } from 'src/types/global'

export default function CompanyList() {
  const intl = useIntl()
  const [searchTerm, setSearchTerm] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const companies = useCompanies()
  const refresh = useFetchCb()
  const { showToast, toastElement } = useToast()

  const handleSaved = () => {
    refresh(ADMIN_URL + '/companies')
    showToast(intl.formatMessage({ id: 'companies.created' }))
  }

  if (!companies || companies.length === 0) {
    return (
      <div className="container">
        <h1>
          <FormattedMessage id="companies.empty" />
        </h1>
      </div>
    )
  }

  const filteredCompanies = companies.filter((company: Company) => {
    const term = searchTerm.toLowerCase()
    const matchName = company.name.toLowerCase().includes(term)
    const matchNif = company.nif.toLowerCase().includes(term)

    return matchName || matchNif
  })

  return (
    <>
      <div className="companies-toolbar">
        <search>
          <label className="search-container">
            <span className="icon icon-search" />{' '}
            <input
              type="search"
              className="search-input"
              placeholder="Nombre o NIF..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>
        </search>
        <button
          type="button"
          className="highlight-button"
          onClick={() => setIsCreating(true)}
        >
          <span className="icon icon-plus" />
          <span>
            <FormattedMessage id="companies.new" />
          </span>
        </button>
      </div>
      {filteredCompanies.length === 0 ? (
        <p className="empty-state">
          <span>
            <FormattedMessage id="search.no-results" />{' '}
          </span>
          <strong>
            <em>{searchTerm}</em>
          </strong>
        </p>
      ) : (
        <ul className="company-list">
          {filteredCompanies.map((company) => (
            <li key={company.id} className="company-card">
              <Link to={`/company/${company.id}`} className="company-link">
                <h2 className="company-name">
                  <span className="icon icon-home-stroke" />
                  <span>{company.name}</span>
                </h2>
                <div className="company-details">
                  <div>
                    <strong>NIF:</strong> {company.nif}
                  </div>
                  <div className="company-date">
                    <span className="icon icon-calendar" />
                    <strong>
                      <FormattedDate
                        value={company.created}
                        day="numeric"
                        month="numeric"
                        year="2-digit"
                      />
                    </strong>
                    <span className="icon icon-clock" />
                    <strong>
                      <FormattedTime value={company.created} />
                    </strong>
                  </div>
                </div>
                {company.tags.length > 0 && (
                  <ul className="tag-chips">
                    {company.tags.map((tag) => (
                      <li key={tag}>{tag}</li>
                    ))}
                  </ul>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}

      {isCreating && (
        <CompanyForm
          onClose={() => setIsCreating(false)}
          onSaved={handleSaved}
        />
      )}
      {toastElement}
    </>
  )
}
